import { Sprite, Texture } from "pixi.js"
import { logger } from "../../../../logger"
import { SurfaceObject } from "./tile_effect_system"

export interface HoleOutlineConfig {
    thickness: number
    color: number
    alpha: number
    enabled: boolean
}

export interface ScuffMarkConfig {
    enabled: boolean
    count: number
    color: number
    alpha: number
    minSize: number
    maxSize: number
    roughness: number // 0-1, affects how jagged the scuffs are
}

export interface DentConfig {
    enabled: boolean
    count: number
    color: number
    alpha: number
    minSize: number
    maxSize: number
    depth: number // 0-1, affects how deep the dent shadow appears
}

export interface HoleEffectOptions {
    damagePct: number
    x: number
    y: number
    outlineConfig?: Partial<HoleOutlineConfig>
    scuffConfig?: Partial<ScuffMarkConfig>
    dentConfig?: Partial<DentConfig>
}

// A single scratch canvas seeded with a sprite's original texture. Every hole/scuff/dent
// for that sprite is drawn onto this one canvas and uploaded as a single texture at the end,
// instead of allocating a canvas + getImageData readback + texture upload per individual mark.
interface DamageCanvas {
    canvas: HTMLCanvasElement
    ctx: CanvasRenderingContext2D
    width: number
    height: number
}

export class HoleEffect {
    private static readonly DEFAULT_OUTLINE_CONFIG: HoleOutlineConfig = {
        thickness: 0.8,
        color: 0x1a1a1a, // Very dark gray/black
        alpha: 0.7,
        enabled: true,
    }

    private static readonly DEFAULT_SCUFF_CONFIG: ScuffMarkConfig = {
        enabled: true,
        count: 3,
        color: 0xff00ff, // Magenta for visibility
        alpha: 0.8,
        minSize: 0.05, // 5% of texture size
        maxSize: 0.15, // 15% of texture size
        roughness: 0.7,
    }

    private static readonly DEFAULT_DENT_CONFIG: DentConfig = {
        enabled: true,
        count: 2,
        color: 0x1a1a1a, // Very dark gray/black
        alpha: 0.4,
        minSize: 0.08, // 8% of texture size
        maxSize: 0.2, // 20% of texture size
        depth: 0.6,
    }

    // Make cache persistent across rooms
    private static readonly GLOBAL_DAMAGED_SPRITES_CACHE: Map<string, Sprite[]> = new Map()
    private static maxGlobalCacheSize = 5000 // Global cache size limit
    private static cacheHits = 0
    private static cacheMisses = 0

    private outlineConfig: HoleOutlineConfig = { ...HoleEffect.DEFAULT_OUTLINE_CONFIG }
    private scuffConfig: ScuffMarkConfig = { ...HoleEffect.DEFAULT_SCUFF_CONFIG }
    private dentConfig: DentConfig = { ...HoleEffect.DEFAULT_DENT_CONFIG }
    private randomNumberGenerator: () => number
    private roomId: string
    private rngCache: Map<string, number> = new Map()

    // Track intact art separately so repairing can remove damage, including on pooled sprites.
    private lastDamagedTextures = new WeakMap<Sprite, Texture>()
    private sourceTextures = new WeakMap<Sprite, Texture>()
    // Cache original textures by x,y coordinates (room-specific).
    private originalTexturesByLocation: Map<string, Texture[]> = new Map()

    constructor(roomId: string, randomNumberGenerator: () => number) {
        this.roomId = roomId
        this.randomNumberGenerator = randomNumberGenerator
    }

    /**
     * Update the random number generator and room context
     * Only clear room-specific caches, keep global damage cache
     */
    public updateRandomContext = (roomId: string, randomNumberGenerator: () => number): void => {
        this.roomId = roomId
        this.randomNumberGenerator = randomNumberGenerator
        this.rngCache.clear()
        this.originalTexturesByLocation.clear() // Clear room-specific texture cache

        // DON'T clear the global damaged sprites cache - keep it persistent!
    }

    /**
     * The art a sprite showed before damage cut holes in it: its own texture while it is intact or repaired, or since a
     * pooled sprite was handed other art.
     */
    public intactTexture = (sprite: Sprite): Texture =>
        sprite.texture === this.lastDamagedTextures.get(sprite) ? this.sourceTextures.get(sprite) ?? sprite.texture : sprite.texture

    /**
     * Cache original textures by x,y location for incremental damage
     */
    public cacheOriginalTexturesByLocation = (sprites: Sprite[], x: number, y: number): void => {
        const locationKey = `${this.roomId}-${x},${y}`

        // Only cache if not already cached to avoid overwriting
        if (!this.originalTexturesByLocation.has(locationKey)) {
            const originalTextures = sprites.map(sprite => sprite.texture)
            this.originalTexturesByLocation.set(locationKey, originalTextures)
        }
    }

    /**
     * Configure hole outline appearance
     */
    public configureHoleOutlines = (config: Partial<HoleOutlineConfig>): void => {
        this.outlineConfig = { ...this.outlineConfig, ...config }
    }

    /**
     * Enable or disable hole outlines
     */
    public setHoleOutlinesEnabled = (enabled: boolean): void => {
        this.outlineConfig.enabled = enabled
    }

    /**
     * Get current hole outline configuration
     */
    public getHoleOutlineConfig = (): HoleOutlineConfig => {
        return { ...this.outlineConfig }
    }

    /**
     * Configure scuff mark appearance
     */
    public configureScuffMarks = (config: Partial<ScuffMarkConfig>): void => {
        this.scuffConfig = { ...this.scuffConfig, ...config }
    }

    /**
     * Enable or disable scuff marks
     */
    public setScuffMarksEnabled = (enabled: boolean): void => {
        this.scuffConfig.enabled = enabled
    }

    /**
     * Get current scuff mark configuration
     */
    public getScuffMarkConfig = (): ScuffMarkConfig => {
        return { ...this.scuffConfig }
    }

    /**
     * Configure dent appearance
     */
    public configureDents = (config: Partial<DentConfig>): void => {
        this.dentConfig = { ...this.dentConfig, ...config }
    }

    /**
     * Enable or disable dents
     */
    public setDentsEnabled = (enabled: boolean): void => {
        this.dentConfig.enabled = enabled
    }

    /**
     * Get current dent configuration
     */
    public getDentConfig = (): DentConfig => {
        return { ...this.dentConfig }
    }

    /**
     * Normalize damage percentage to nearest 5%
     */
    private normalizeDamagePct = (damagePct: number): number => {
        return Math.round(damagePct * 20) / 20 // Round to nearest 5%
    }

    /**
     * Create cache key that's room-agnostic for global cache
     */
    private createGlobalCacheKey = (surfaceObject: SurfaceObject, normalizedDamagePct: number): string => {
        // The cached value is the ENTIRE spriteSet, re-applied to sprites by positional index
        // on a hit. So the key must uniquely identify the full per-sprite composition, not just
        // one surface-level tile id. A vertical wall is two sprites (cap + base) that share a
        // single parent -- surfaceObject.tilesetTileId only records the BASE tile, so keying on
        // it alone made every wall with the same base tile collide regardless of its cap or
        // sprite ordering. On a hit, cap and base textures were then stamped by index onto a
        // differently-composed wall, making the base cell render as a cap. Build the key from
        // each sprite's own texture identity, in order, so a collision guarantees an identical
        // composition and the by-index texture copy is always correct.
        const spriteKey = (surfaceObject.spriteSet || [])
            .map(sprite => {
                const texture = sprite?.texture
                if (!texture) {
                    return "x"
                }
                const id =
                    texture.textureCacheIds && texture.textureCacheIds.length
                        ? texture.textureCacheIds[0]
                        : `${texture.baseTexture?.uid}`
                const frame = texture.frame
                    ? `${texture.frame.x},${texture.frame.y},${texture.frame.width},${texture.frame.height}`
                    : ""
                return `${id}:${frame}`
            })
            .join("|")
        // Room-agnostic key for global cache
        return `${spriteKey}-${normalizedDamagePct}`
    }

    /**
     * Generate dynamic holes, scuff marks, and dents on sprites based on damage percentage
     * Now accepts SurfaceObject for better caching and tile identification
     *
     * @param surfaceObject - SurfaceObject containing sprites and tile information
     * @param options - Configuration options including damage percentage and effect configs
     *
     * Example usage:
     * ```typescript
     * const holeEffect = new HoleEffect(roomId, randomNumberGenerator);
     *
     * // Configure scuff marks
     * holeEffect.configureScuffMarks({
     *   enabled: true,
     *   count: 5,
     *   color: 0x333333,
     *   alpha: 0.7,
     *   minSize: 0.08,
     *   maxSize: 0.20,
     *   roughness: 0.8
     * });
     *
     * // Configure dents
     * holeEffect.configureDents({
     *   enabled: true,
     *   count: 3,
     *   color: 0x1a1a1a,
     *   alpha: 0.5,
     *   minSize: 0.10,
     *   maxSize: 0.25,
     *   depth: 0.7
     * });
     *
     * // Apply effects
     * holeEffect.generateDynamicHoles(surfaceObject, {
     *   damagePct: 0.5, // 50% damage
     *   x: 100,
     *   y: 100,
     *   scuffConfig: { count: 4, roughness: 0.9 },
     *   dentConfig: { count: 2, depth: 0.8 }
     * });
     * ```
     */
    private generateDynamicHoles = (surfaceObject: SurfaceObject, options: HoleEffectOptions): void => {
        const { damagePct, x, y, outlineConfig, scuffConfig, dentConfig } = options
        // This can run deferred (a shake's onComplete, or applyDamageVisualizationToExistingTiles after a
        // full rerender). In between, a concurrent full rerender / incremental repaint can have destroyed
        // this surface's sprites -- reading `.x` (generateAndCacheDamagedSprites) or `.texture`
        // (createSpriteCanvas) on a destroyed sprite (transform null) throws. Prune to live sprites in
        // place so the cache key and the per-index texture copy stay consistent, then bail if none remain.
        if (surfaceObject.spriteSet) {
            surfaceObject.spriteSet = surfaceObject.spriteSet.filter(s => s && !s.destroyed && (s as any).transform)
        }
        // Sprites are already in correct Y-order from processSurfaceId insertion
        const sprites = surfaceObject.spriteSet
        if (!sprites || sprites.length === 0) {
            return
        }

        // Always rebuild holes from the intact artwork. Drawing over already damaged textures
        // can only add holes and cannot heal them as HP rises.
        sprites.forEach(sprite => {
            if (!this.sourceTextures.has(sprite) ||
                (sprite.texture !== this.lastDamagedTextures.get(sprite) && sprite.texture !== this.sourceTextures.get(sprite))) {
                this.sourceTextures.set(sprite, sprite.texture)
            }
            sprite.texture = this.sourceTextures.get(sprite)!
        })
        if (damagePct <= 0) return

        // Apply configs if provided
        if (outlineConfig) {
            this.configureHoleOutlines(outlineConfig)
        }
        if (scuffConfig) {
            this.configureScuffMarks(scuffConfig)
        }
        if (dentConfig) {
            this.configureDents(dentConfig)
        }

        // Normalize damage to nearest 5%
        const normalizedDamagePct = this.normalizeDamagePct(damagePct)

        // Check global cache first (room-agnostic)
        const globalCacheKey = this.createGlobalCacheKey(surfaceObject, normalizedDamagePct)
        const cachedDamagedSprites = HoleEffect.GLOBAL_DAMAGED_SPRITES_CACHE.get(globalCacheKey)

        if (cachedDamagedSprites) {
            HoleEffect.cacheHits++
            // Use cached sprites - just copy the textures
            sprites.forEach((sprite, index) => {
                if (cachedDamagedSprites[index]) {
                    sprite.texture = cachedDamagedSprites[index].texture
                    this.lastDamagedTextures.set(sprite, sprite.texture)
                }
            })
            return
        }

        HoleEffect.cacheMisses++

        // Generate new damaged sprites and cache them globally
        const damagedSprites = this.generateAndCacheDamagedSprites(surfaceObject, normalizedDamagePct, globalCacheKey)

        // Apply the damaged textures to the input sprites
        sprites.forEach((sprite, index) => {
            if (damagedSprites[index]) {
                sprite.texture = damagedSprites[index].texture
                this.lastDamagedTextures.set(sprite, sprite.texture)
            }
        })
    }

    /**
     * Generate and cache damaged sprites for global reuse
     */
    private generateAndCacheDamagedSprites = (
        surfaceObject: any,
        normalizedDamagePct: number,
        globalCacheKey: string,
    ): Sprite[] => {
        const sprites = surfaceObject.spriteSet

        // Create copies of the original sprites for caching
        const damagedSprites = sprites.map(sprite => {
            const copy = new Sprite(sprite.texture)
            copy.x = sprite.x
            copy.y = sprite.y
            copy.zIndex = sprite.zIndex
            return copy
        })

        // Apply damage effects to the copies using the original incremental method
        this.applyDamageToSprites(damagedSprites, normalizedDamagePct, 0, 0)

        // Cache the damaged sprites globally
        HoleEffect.GLOBAL_DAMAGED_SPRITES_CACHE.set(globalCacheKey, damagedSprites)

        // Manage cache size
        this.evictOldestGlobalCache()

        return damagedSprites
    }

    /**
     * Apply damage effects to sprites (extracted from original generateDynamicHoles logic)
     */
    private applyDamageToSprites = (sprites: Sprite[], damagePct: number, x: number, y: number): void => {
        // Apply damage incrementally from 10% to current damage level
        const currentDamageStep = Math.floor(damagePct * 20) / 20 // Round to nearest 5%
        const maxDamageStep = currentDamageStep

        // Build ONE canvas per sprite up front. Every hole/scuff/dent for that sprite is drawn
        // onto this single canvas and uploaded as a single texture at the end. This replaces the
        // previous approach that allocated a fresh canvas, ran a full-canvas getImageData()
        // readback (a synchronous GPU->CPU stall) and a Texture.from() upload for EVERY individual
        // mark -- the dominant cost of this path on low-end/integrated GPUs.
        const canvases = sprites.map(sprite => this.createSpriteCanvas(sprite))

        // Walk up by 10% damage increments up to current damage
        for (let stepDamage = 0.1; stepDamage <= maxDamageStep; stepDamage += 0.1) {
            const stepDamagePct = Math.min(stepDamage, maxDamageStep)

            // Generate holes for this damage step
            const numHoles = Math.floor(stepDamagePct * 8) + 1 // 1-17 holes based on damage

            // Distribute holes evenly across all sprites in the set
            const holesPerSprite = Math.max(1, Math.floor(numHoles / sprites.length))
            const remainingHoles = numHoles - holesPerSprite * sprites.length

            for (let spriteIndex = 0; spriteIndex < sprites.length; spriteIndex++) {
                const dc = canvases[spriteIndex]
                if (!dc) continue
                const holesForThisSprite = holesPerSprite + (spriteIndex < remainingHoles ? 1 : 0)

                // Create holes for this damage step
                for (let i = 0; i < holesForThisSprite; i++) {
                    this.createHoleInSprite(dc, stepDamagePct, x, y, spriteIndex, i)
                }

                // Add scuff marks and dents for this damage step
                this.addScuffMarksToSprite(dc, stepDamagePct, x, y, spriteIndex)
                this.addDentsToSprite(dc, stepDamagePct, x, y, spriteIndex)
            }
        }

        // Upload each modified canvas as a single texture (one GPU upload per sprite).
        sprites.forEach((sprite, index) => {
            const dc = canvases[index]
            if (dc) {
                sprite.texture = Texture.from(dc.canvas)
            }
        })
    }

    /**
     * Build a scratch canvas seeded with the sprite's original texture. All damage marks for
     * the sprite are drawn onto this one canvas; marks that must stay within the sprite's opaque
     * silhouette use the "source-atop" composite op rather than a per-pixel getImageData mask.
     */
    private createSpriteCanvas = (sprite: Sprite): DamageCanvas | null => {
        try {
            const texture = sprite.texture
            const baseTexture = texture.baseTexture
            const imageSource = (baseTexture.resource as any)?.source as HTMLImageElement
            if (!imageSource) {
                console.warn("Cannot access image source for texture manipulation")
                return null
            }

            const canvas = document.createElement("canvas")
            canvas.width = texture.width
            canvas.height = texture.height
            const ctx = canvas.getContext("2d")!

            // Draw the original texture to canvas using the frame coordinates
            const frame = texture.frame
            ctx.drawImage(imageSource, frame.x, frame.y, frame.width, frame.height, 0, 0, frame.width, frame.height)

            return { canvas, ctx, width: frame.width, height: frame.height }
        } catch (error) {
            console.warn("Failed to create sprite canvas:", error)
            return null
        }
    }

    /**
     * Convenience method that matches MapRenderingManager's interface
     * @param surfaceObject - SurfaceObject containing sprites and tile information
     * @param hpPct - Health percentage (0-1, where 1 is full health)
     * @param x - X coordinate for seeded randomness
     * @param y - Y coordinate for seeded randomness
     */
    public generateDynamicHolesFromHp = (surfaceObject: SurfaceObject, hpPct: number, x: number, y: number): void => {
        const damagePct = 1.0 - hpPct
        this.generateDynamicHoles(surfaceObject, {
            damagePct,
            x,
            y,
        })
    }

    /**
     * Create a single hole in a sprite
     */
    private createHoleInSprite = (
        dc: DamageCanvas,
        damagePct: number,
        x: number,
        y: number,
        spriteIndex: number,
        holeIndex: number,
    ): void => {
        // Use texture frame dimensions (not full texture) for hole positioning
        const textureWidth = dc.width
        const textureHeight = dc.height
        const holeSize = Math.min(textureWidth, textureHeight) * (0.09 + damagePct * 0.12) // 9-21% of texture size (50% larger holes)

        // Position hole randomly across the frame area, but keep it within bounds
        const maxRadius = Math.min(holeSize, textureWidth, textureHeight) / 2

        // Use seeded randomness for consistent positioning
        const centerX = this.randomIntBetweenRng(
            Math.ceil(maxRadius),
            Math.floor(textureWidth - maxRadius),
            `hole-center-x-${spriteIndex}-${holeIndex}-${x}-${y}`,
        )
        const centerY = this.randomIntBetweenRng(
            Math.ceil(maxRadius),
            Math.floor(textureHeight - maxRadius),
            `hole-center-y-${spriteIndex}-${holeIndex}-${x}-${y}`,
        )

        const sides = 3 + this.randomIntBetweenRng(0, 5, `hole-sides-${spriteIndex}-${holeIndex}-${x}-${y}`) // 3-5 sides
        const radius =
            holeSize *
            (0.4 + this.randomIntBetweenRng(0, 60, `hole-radius-${spriteIndex}-${holeIndex}-${x}-${y}`) / 100) // 40-100% of hole size

        // Generate polygon points for the hole using seeded randomness
        const points: number[] = []
        for (let j = 0; j < sides; j++) {
            // Use seeded randomness for angles and radii
            const baseAngle = (j / sides) * Math.PI * 2
            const angleVariation =
                (this.randomIntBetweenRng(-50, 50, `hole-angle-${spriteIndex}-${holeIndex}-${j}-${x}-${y}`) / 100) *
                Math.PI
            const angle = baseAngle + angleVariation
            const pointRadius =
                radius *
                (0.5 +
                    this.randomIntBetweenRng(0, 50, `hole-point-radius-${spriteIndex}-${holeIndex}-${j}-${x}-${y}`) /
                        100) // 50-100% of radius

            const pointX = Math.max(0, Math.min(textureWidth - 1, centerX + Math.cos(angle) * pointRadius))
            const pointY = Math.max(0, Math.min(textureHeight - 1, centerY + Math.sin(angle) * pointRadius))
            points.push(pointX, pointY)
        }

        // Cut the hole into the shared canvas (also draws the outline)
        this.cutHoleOnCtx(dc.ctx, points)
    }

    /**
     * Add scuff marks to a sprite
     */
    private addScuffMarksToSprite = (
        dc: DamageCanvas,
        damagePct: number,
        x: number,
        y: number,
        spriteIndex: number,
    ): void => {
        if (!this.scuffConfig.enabled) return

        // Calculate number of scuffs based on damage
        const scuffCount = Math.floor(this.scuffConfig.count * (0.5 + damagePct * 0.5)) // 50-100% of base count

        for (let i = 0; i < scuffCount; i++) {
            this.createScuffMarkInSprite(dc, damagePct, x, y, spriteIndex, i)
        }
    }

    /**
     * Create a single scuff mark in a sprite
     */
    private createScuffMarkInSprite = (
        dc: DamageCanvas,
        damagePct: number,
        x: number,
        y: number,
        spriteIndex: number,
        scuffIndex: number,
    ): void => {
        const textureWidth = dc.width
        const textureHeight = dc.height

        // Calculate scuff size based on damage and config
        const baseSize =
            textureWidth *
            (this.scuffConfig.minSize + damagePct * (this.scuffConfig.maxSize - this.scuffConfig.minSize))
        const scuffSize =
            baseSize *
            (0.8 + this.randomIntBetweenRng(0, 40, `scuff-size-${spriteIndex}-${scuffIndex}-${x}-${y}`) / 100) // 80-120% of base size

        // Position scuff randomly across the frame area
        const maxRadius = scuffSize / 2
        const centerX = this.randomIntBetweenRng(
            Math.ceil(maxRadius),
            Math.floor(textureWidth - maxRadius),
            `scuff-center-x-${spriteIndex}-${scuffIndex}-${x}-${y}`,
        )
        const centerY = this.randomIntBetweenRng(
            Math.ceil(maxRadius),
            Math.floor(textureHeight - maxRadius),
            `scuff-center-y-${spriteIndex}-${scuffIndex}-${x}-${y}`,
        )

        // Generate scuff as radial crack pattern with more randomness
        const crackRadius =
            scuffSize *
            (0.3 + this.randomIntBetweenRng(0, 40, `crack-radius-${spriteIndex}-${scuffIndex}-${x}-${y}`) / 100) // 30-70% of scuff size
        const crackCount = 3 + this.randomIntBetweenRng(0, 4, `crack-count-${spriteIndex}-${scuffIndex}-${x}-${y}`) // 3-7 cracks

        // Draw scuff mark onto the shared canvas
        this.drawScuffMarkOnCtx(dc.ctx, centerX, centerY, crackRadius, crackCount, spriteIndex, scuffIndex, x, y)
    }

    /**
     * Add dents to a sprite
     */
    private addDentsToSprite = (dc: DamageCanvas, damagePct: number, x: number, y: number, spriteIndex: number): void => {
        if (!this.dentConfig.enabled) return

        // Calculate number of dents based on damage
        const dentCount = Math.floor(this.dentConfig.count * (0.3 + damagePct * 0.7)) // 30-100% of base count

        for (let i = 0; i < dentCount; i++) {
            this.createDentInSprite(dc, damagePct, x, y, spriteIndex, i)
        }
    }

    /**
     * Create a single dent in a sprite
     */
    private createDentInSprite = (
        dc: DamageCanvas,
        damagePct: number,
        x: number,
        y: number,
        spriteIndex: number,
        dentIndex: number,
    ): void => {
        const textureWidth = dc.width
        const textureHeight = dc.height

        // Calculate dent size based on damage and config
        const baseSize =
            textureWidth * (this.dentConfig.minSize + damagePct * (this.dentConfig.maxSize - this.dentConfig.minSize))
        const dentSize =
            baseSize * (0.7 + this.randomIntBetweenRng(0, 60, `dent-size-${spriteIndex}-${dentIndex}-${x}-${y}`) / 100) // 70-130% of base size

        // Position dent randomly across the frame area
        const maxRadius = dentSize / 2
        const centerX = this.randomIntBetweenRng(
            Math.ceil(maxRadius),
            Math.floor(textureWidth - maxRadius),
            `dent-center-x-${spriteIndex}-${dentIndex}-${x}-${y}`,
        )
        const centerY = this.randomIntBetweenRng(
            Math.ceil(maxRadius),
            Math.floor(textureHeight - maxRadius),
            `dent-center-y-${spriteIndex}-${dentIndex}-${x}-${y}`,
        )

        // Generate dent shape (oval with shadow effect)
        const points = this.generateDentShape(
            centerX,
            centerY,
            dentSize,
            spriteIndex,
            dentIndex,
            x,
            y,
            textureWidth,
            textureHeight,
        )

        // Draw dent onto the shared canvas
        this.drawDentOnCtx(dc.ctx, points, centerX, centerY, dentSize)
    }

    /**
     * Generate dent shape points (oval)
     */
    private generateDentShape = (
        centerX: number,
        centerY: number,
        size: number,
        spriteIndex: number,
        dentIndex: number,
        x: number,
        y: number,
        textureWidth: number,
        textureHeight: number,
    ): number[] => {
        const points: number[] = []
        const segments = 16
        const radiusX = size * 0.4 // 40% of size for width
        const radiusY = size * 0.3 // 30% of size for height

        // Generate oval points
        for (let i = 0; i <= segments; i++) {
            const angle = (i / segments) * Math.PI * 2
            const pointX = centerX + Math.cos(angle) * radiusX
            const pointY = centerY + Math.sin(angle) * radiusY
            points.push(
                Math.max(0, Math.min(textureWidth - 1, pointX)),
                Math.max(0, Math.min(textureHeight - 1, pointY)),
            )
        }

        return points
    }

    /**
     * Cut a hole into the shared canvas and stroke its outline.
     *
     * The outline is drawn with the "source-atop" composite op so it only lands on pixels that
     * are still opaque -- this reproduces the old "mask the outline by the sprite's alpha" result
     * without the getImageData/putImageData readback + per-pixel JS loop it used to require.
     */
    private cutHoleOnCtx = (ctx: CanvasRenderingContext2D, holePoints: number[]): void => {
        try {
            const buildPath = (): void => {
                ctx.beginPath()
                ctx.moveTo(holePoints[0], holePoints[1])
                for (let i = 2; i < holePoints.length; i += 2) {
                    ctx.lineTo(holePoints[i], holePoints[i + 1])
                }
                ctx.closePath()
            }

            // Cut out the hole.
            ctx.save()
            ctx.globalCompositeOperation = "destination-out"
            buildPath()
            ctx.fill()
            ctx.restore()

            // Draw the outline, clipped to the remaining opaque silhouette.
            if (this.outlineConfig.enabled) {
                ctx.save()
                ctx.globalCompositeOperation = "source-atop"
                ctx.strokeStyle = `rgba(${this.getColorComponents(this.outlineConfig.color)}, ${
                    this.outlineConfig.alpha
                })`
                ctx.lineWidth = this.outlineConfig.thickness
                ctx.lineCap = "round"
                ctx.lineJoin = "round"
                buildPath()
                ctx.stroke()
                ctx.restore()
            }
        } catch (error) {
            console.warn("Failed to cut hole on canvas:", error)
        }
    }

    /**
     * Draw a scuff mark (radial cracks) directly onto the shared canvas.
     *
     * Uses "source-atop" so the cracks are clipped to the sprite's opaque silhouette -- the same
     * result the old per-pixel alpha mask produced, minus the getImageData readback + temp canvas.
     */
    private drawScuffMarkOnCtx = (
        ctx: CanvasRenderingContext2D,
        centerX: number,
        centerY: number,
        crackRadius: number,
        crackCount: number,
        spriteIndex: number,
        scuffIndex: number,
        x: number,
        y: number,
    ): void => {
        try {
            ctx.save()
            ctx.globalCompositeOperation = "source-atop"
            ctx.strokeStyle = `rgba(${this.getColorComponents(this.scuffConfig.color)}, ${this.scuffConfig.alpha})`
            ctx.lineWidth = 1.5
            ctx.lineCap = "round"
            ctx.lineJoin = "round"

            // Draw radial cracks from center point
            for (let i = 0; i < crackCount; i++) {
                // Calculate angle for this crack with more randomness
                const baseAngle = (i / crackCount) * Math.PI * 2
                // Add more randomness to the angle using seeded RNG instead of Date.now()
                const angleVariation =
                    (this.randomIntBetweenRng(-60, 60, `crack-angle-${spriteIndex}-${scuffIndex}-${i}-${x}-${y}`) /
                        100) *
                    Math.PI
                const angle = baseAngle + angleVariation

                // Calculate crack length (with more variation) using seeded RNG instead of Date.now()
                const crackLength =
                    crackRadius *
                    (0.4 +
                        this.randomIntBetweenRng(0, 120, `crack-length-${spriteIndex}-${scuffIndex}-${i}-${x}-${y}`) /
                            100) // 40-160% of radius

                // Calculate end point
                const endX = centerX + Math.cos(angle) * crackLength
                const endY = centerY + Math.sin(angle) * crackLength

                // Draw the crack line
                ctx.beginPath()
                ctx.moveTo(centerX, centerY)
                ctx.lineTo(endX, endY)
                ctx.stroke()
            }

            ctx.restore()
        } catch (error) {
            console.warn("Failed to draw scuff mark on canvas:", error)
        }
    }

    /**
     * Draw a dent (radial shadow gradient + subtle outline) directly onto the shared canvas.
     *
     * Uses "source-atop" so the dent is clipped to the sprite's opaque silhouette -- the same
     * result the old per-pixel alpha mask produced, minus the getImageData readback + temp canvas.
     */
    private drawDentOnCtx = (
        ctx: CanvasRenderingContext2D,
        dentPoints: number[],
        centerX: number,
        centerY: number,
        dentSize: number,
    ): void => {
        try {
            ctx.save()
            ctx.globalCompositeOperation = "source-atop"

            // Create shadow gradient for dent effect
            const gradient = ctx.createRadialGradient(
                centerX - dentSize * 0.1, // Offset shadow slightly
                centerY - dentSize * 0.1,
                0,
                centerX,
                centerY,
                dentSize * 0.5,
            )
            gradient.addColorStop(
                0,
                `rgba(${this.getColorComponents(this.dentConfig.color)}, ${
                    this.dentConfig.alpha * this.dentConfig.depth
                })`,
            )
            gradient.addColorStop(1, `rgba(${this.getColorComponents(this.dentConfig.color)}, 0)`)

            // Build the dent path once, reused for fill + stroke
            ctx.beginPath()
            ctx.moveTo(dentPoints[0], dentPoints[1])
            for (let i = 2; i < dentPoints.length; i += 2) {
                ctx.lineTo(dentPoints[i], dentPoints[i + 1])
            }
            ctx.closePath()

            // Draw the dent shadow
            ctx.fillStyle = gradient
            ctx.fill()

            // Add a subtle outline
            ctx.strokeStyle = `rgba(${this.getColorComponents(this.dentConfig.color)}, ${this.dentConfig.alpha * 0.8})`
            ctx.lineWidth = 1
            ctx.lineCap = "round"
            ctx.lineJoin = "round"
            ctx.stroke()

            ctx.restore()
        } catch (error) {
            console.warn("Failed to draw dent on canvas:", error)
        }
    }

    /**
     * Convert color number to RGB components string
     */
    private getColorComponents = (color: number): string => {
        const r = (color >> 16) & 0xff
        const g = (color >> 8) & 0xff
        const b = color & 0xff
        return `${r}, ${g}, ${b}`
    }

    /**
     * Generate random integer between min and max using seeded randomness
     * This matches the exact same format as MapRenderingManager.randomIntBetweenRng
     */
    private randomIntBetweenRng = (min: number, max: number, seed?: string): number => {
        let randomFloat
        if (!seed) {
            randomFloat = this.randomNumberGenerator ? this.randomNumberGenerator() : Math.random()
        } else {
            const toUse = `${seed}-${min}-${max}-${this.roomId}`
            randomFloat = this.rngCache.get(toUse)
            if (randomFloat === undefined) {
                randomFloat = this.randomNumberGenerator ? this.randomNumberGenerator() : Math.random()
                this.rngCache.set(toUse, randomFloat)
            }
        }

        return Math.floor(randomFloat * (max - min + 1) + min)
    }

    /**
     * Flush the texture cache (useful when full map rerender starts)
     */
    public flushCache(): void {
        this.originalTexturesByLocation.clear()
    }

    /**
     * Evict oldest entries from global cache when it gets too large
     */
    private evictOldestGlobalCache = (): void => {
        if (HoleEffect.GLOBAL_DAMAGED_SPRITES_CACHE.size > HoleEffect.maxGlobalCacheSize) {
            // Remove oldest entries (Map maintains insertion order)
            const entriesToRemove = HoleEffect.GLOBAL_DAMAGED_SPRITES_CACHE.size - HoleEffect.maxGlobalCacheSize
            const keysToRemove = Array.from(HoleEffect.GLOBAL_DAMAGED_SPRITES_CACHE.keys()).slice(0, entriesToRemove)

            keysToRemove.forEach(key => {
                const sprites = HoleEffect.GLOBAL_DAMAGED_SPRITES_CACHE.get(key)
                if (sprites) {
                    sprites.forEach(sprite => sprite.destroy())
                }
                HoleEffect.GLOBAL_DAMAGED_SPRITES_CACHE.delete(key)
            })
        }
    }

    /**
     * Get cache statistics
     */
    public static getCacheStats = (): { hits: number; misses: number; size: number; hitRate: number } => {
        const total = HoleEffect.cacheHits + HoleEffect.cacheMisses
        return {
            hits: HoleEffect.cacheHits,
            misses: HoleEffect.cacheMisses,
            size: HoleEffect.GLOBAL_DAMAGED_SPRITES_CACHE.size,
            hitRate: total > 0 ? HoleEffect.cacheHits / total : 0,
        }
    }

    /**
     * Clear global cache (useful for memory management or testing)
     */
    public static clearGlobalCache = (): void => {
        HoleEffect.GLOBAL_DAMAGED_SPRITES_CACHE.forEach(sprites => {
            sprites.forEach(sprite => sprite.destroy())
        })
        HoleEffect.GLOBAL_DAMAGED_SPRITES_CACHE.clear()
        HoleEffect.cacheHits = 0
        HoleEffect.cacheMisses = 0
    }

    /**
     * Clean up resources (only clear room-specific caches)
     */
    public destroy(): void {
        this.rngCache.clear()
        this.originalTexturesByLocation.clear()
        // DON'T clear global cache - it's shared across instances
    }
}
